import React, { useState, useEffect, useCallback, createContext, useContext } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import Sidebar from './components/Sidebar';
import Toast from './components/Toast';
import ProtectedRoute from './components/ProtectedRoute';
import { AuthProvider } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import Dashboard from './pages/Dashboard';
import ProjectsPage from './pages/ProjectsPage';
import ProjectDetail from './pages/ProjectDetail';
import TestExecution from './pages/TestExecution';
import TraceabilityMatrix from './pages/TraceabilityMatrix';
import Reports from './pages/Reports';
import Preview from './pages/Preview';
import AIGenerate from './pages/AIGenerate';
import Configuration from './pages/Configuration';
import Monitoring from './pages/Monitoring';
import LoginPage from './pages/LoginPage';
import UserManagement from './pages/UserManagement';
import SquashTracking from './pages/SquashTracking';
import Profile from './pages/Profile';
import CampaignsPage from './pages/CampaignsPage';
import CampaignDetail from './pages/CampaignDetail';
import DefectsPage from './pages/DefectsPage';
import AuditPage from './pages/AuditPage';
import SystemLogsPage from './pages/SystemLogsPage';
import AISourcesPage from './pages/AISourcesPage';
import GlobalSearchModal from './components/GlobalSearchModal';
import ErrorBoundary from './components/ErrorBoundary';
import { getProjects, getProject } from './services/api';

// Toast context
const ToastContext = createContext();
export function useToast() { return useContext(ToastContext); }

function AppLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [projects, setProjects] = useState([]);
  const [currentProject, setCurrentProject] = useState(null);
  const [toasts, setToasts] = useState([]);
  const [showSearch, setShowSearch] = useState(false);
  const location = useLocation();
  const projectMatch = /^\/projects\/(\d+)/.exec(location.pathname);
  const projectId = projectMatch ? projectMatch[1] : null;

  const addToast = useCallback((type, message, title) => {
    const id = Date.now() + Math.random();
    setToasts((prev) => [...prev, { id, type, message, title }]);
  }, []);
  const removeToast = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const loadProjects = useCallback(async () => {
    try {
      const { data } = await getProjects();
      setProjects(data);
    } catch (e) { }
  }, []);

  useEffect(() => { loadProjects(); }, [loadProjects]);

  useEffect(() => {
    if (projectId) {
      getProject(projectId).then(({ data }) => setCurrentProject(data)).catch(() => { });
    } else {
      setCurrentProject(null);
    }
  }, [projectId]);

  useEffect(() => {
    if (location.pathname === '/' || location.pathname === '/projects') loadProjects();
  }, [location.pathname, loadProjects]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        setShowSearch(true);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  return (
    <ToastContext.Provider value={addToast}>
      <div className={`app-layout ${sidebarOpen ? '' : 'sidebar-collapsed'}`}>
        <Sidebar
          projects={projects}
          currentProject={currentProject}
          sidebarOpen={sidebarOpen}
          onToggleSidebar={() => setSidebarOpen(!sidebarOpen)}
        />
        <main className="app-content">
          <Routes>
            <Route path="/" element={<ErrorBoundary><Dashboard onProjectsChange={loadProjects} /></ErrorBoundary>} />
            <Route path="/projects" element={<ErrorBoundary><ProjectsPage onProjectsChange={loadProjects} /></ErrorBoundary>} />
            <Route path="/projects/:id" element={<ErrorBoundary><ProjectDetail /></ErrorBoundary>} />
            <Route path="/projects/:id/execution" element={<ErrorBoundary><TestExecution /></ErrorBoundary>} />
            <Route path="/projects/:id/traceability" element={<ErrorBoundary><TraceabilityMatrix /></ErrorBoundary>} />
            <Route path="/projects/:id/reports" element={<ErrorBoundary><Reports /></ErrorBoundary>} />
            <Route path="/projects/:id/preview" element={<ErrorBoundary><Preview /></ErrorBoundary>} />
            <Route path="/projects/:id/ai" element={<ErrorBoundary><AIGenerate /></ErrorBoundary>} />
            <Route path="/projects/:id/campaigns" element={<ErrorBoundary><CampaignsPage /></ErrorBoundary>} />
            <Route path="/projects/:id/campaigns/:campaignId" element={<ErrorBoundary><CampaignDetail /></ErrorBoundary>} />
            <Route path="/projects/:id/defects" element={<ErrorBoundary><DefectsPage /></ErrorBoundary>} />
            <Route path="/profile" element={<ErrorBoundary><Profile /></ErrorBoundary>} />
            <Route path="/squash-tracking" element={<ErrorBoundary><SquashTracking /></ErrorBoundary>} />
            <Route path="/configuration" element={
              <ProtectedRoute adminOnly>
                <ErrorBoundary><Configuration /></ErrorBoundary>
              </ProtectedRoute>
            } />
            <Route path="/monitoring" element={
              <ProtectedRoute adminOnly>
                <ErrorBoundary><Monitoring /></ErrorBoundary>
              </ProtectedRoute>
            } />
            <Route path="/users" element={
              <ProtectedRoute adminOnly>
                <ErrorBoundary><UserManagement /></ErrorBoundary>
              </ProtectedRoute>
            } />
            <Route path="/admin/audit" element={
              <ProtectedRoute adminOnly>
                <ErrorBoundary><AuditPage /></ErrorBoundary>
              </ProtectedRoute>
            } />
            <Route path="/admin/system-logs" element={
              <ProtectedRoute adminOnly>
                <ErrorBoundary><SystemLogsPage /></ErrorBoundary>
              </ProtectedRoute>
            } />
            {/* Documents sources IA : superadmin uniquement */}
            <Route path="/admin/ai-sources" element={
              <ProtectedRoute superAdminOnly>
                <ErrorBoundary><AISourcesPage /></ErrorBoundary>
              </ProtectedRoute>
            } />
          </Routes>
        </main>
      </div>
      <GlobalSearchModal show={showSearch} onClose={() => setShowSearch(false)} />
      <Toast toasts={toasts} removeToast={removeToast} />
    </ToastContext.Provider>
  );
}

export default function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <BrowserRouter>
          <AuthProvider>
            <Routes>
              <Route path="/login" element={<LoginPage />} />
              <Route path="/*" element={
                <ProtectedRoute>
                  <AppLayout />
                </ProtectedRoute>
              } />
            </Routes>
          </AuthProvider>
        </BrowserRouter>
      </ThemeProvider>
    </ErrorBoundary>
  );
}
